import { Component, ComponentFactoryResolver, ComponentRef, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CheckboxFieldComponent, DateUtilsService, NumberFieldComponent, PhoneNumberFieldComponent, StringFieldComponent, TextLabelComponent, TimeUtilsService, TranslationService } from '@sdbn/common';
import { TransportDetail, TransportDetailDefinition, TransportDetailLayoutFieldReference, TransportDetailMode } from 'src/tmc-yard/common/configuration/transport.configuration';
import { DataType } from 'src/tmc-yard/common/model/common.model';
import { Transport } from 'src/tmc-yard/common/model/transport.model';
import { CarrierAutoCompleteFieldComponent } from '../../../fields/carrier-auto-complete-field/carrier-auto-complete-field.component';
import { DataProtectionFieldComponent } from '../../../fields/data-protectionn-rule-field/data-protection-field.component';
import { CommunicationLanguageFieldComponent } from '../../../fields/communication-language-field/communication-language-field.component';
import { Subject, Subscription } from 'rxjs';
import { DictionaryFieldComponent } from '../../../fields/dictionary-field/dictionary-field.component';
import { DictionarySelectFieldComponent } from '../../../fields/dictionary-select-field/dictionary-select-field.component';

@Component({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TransportDetailsFieldWrapperComponent),
      multi: true,
    }
  ],
  selector: 'app-transport-details-field-wrapper',
  templateUrl: './transport-details-field-wrapper.component.html',
  styleUrls: ['./transport-details-field-wrapper.component.scss']
})
export class TransportDetailsFieldWrapperComponent implements OnDestroy, OnInit, ControlValueAccessor {

  fieldReference: TransportDetailLayoutFieldReference;

  field: TransportDetail;

  dictionaryPraefix = 'tmc-yard.entity.transport.';

  transport: Transport;

  transportSubscription: Subscription;


  @Input('field') set setField(value: TransportDetailLayoutFieldReference) {
    this.fieldReference = value;
    this.field = this.findByIdentifier(value.key);
  }

  @Input() mode = TransportDetailMode.READONLY;

  @Input() disableAutoComplete: boolean = false;

  @Input('transport') set setTransport(value: Transport) {

    if (this.transportSubscription != null) {
      this.transportSubscription.unsubscribe();
      this.transportSubscription = null;
    }

    this.transport = value;
    if (this.field != null && this.componentRef != null && value != null) {
      this.setComponentValue();
    }

    if (value != null) {
      this.transportSubscription = value.updated.subscribe(() => {
        this.setComponentValue();
      });
    }

  }

  @Output() valueChange = new Subject<null>();

  @ViewChild('contentRef', { read: ViewContainerRef, static: true }) contentRef: ViewContainerRef;

  valueChangeSubscription: Subscription;

  public readonly formControl = new FormControl(null, []);

  controlValueAccessor: ControlValueAccessor;

  componentRef: ComponentRef<any>;

  onChangeFn: any;
  onTouched: any;
  disabled: boolean;
  value: any;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private translationService: TranslationService,
    private dateUtilsService: DateUtilsService,
    private timeUtilsService: TimeUtilsService
  ) { }

  ngOnDestroy(): void {
    if (this.valueChangeSubscription != null) {
      this.valueChangeSubscription.unsubscribe();
    }
  }

  findByIdentifier(identifier: string): TransportDetail {
    for (const group of TransportDetailDefinition) {
      for (const detail of group.details) {
        if (detail.identifier === identifier) {
          return detail;
        }
      }
    }
    return null;
  };

  ngOnInit(): void {
    this.createComponent();
    this.setComponentValue();
  }

  createComponent() {

    while (this.contentRef.length > 0) {
      this.contentRef.remove(0);
    }

    if (this.fieldReference != null && this.field != null) {

      if (this.field.isEditable && this.mode === TransportDetailMode.EDITING) {
        this.createEditorComponent();
      } else {
        this.createPanelComponent();
      }

    }
  }

  private createEditorComponent() {

    const factory = this.componentFactoryResolver.resolveComponentFactory(this.field.panelDefinitionEditor);
    this.componentRef = this.contentRef.createComponent(factory);

    console.log('component is', this.componentRef);

    if (this.valueChangeSubscription != null) {
      this.valueChangeSubscription.unsubscribe();
    }

    if ('valueChange' in this.componentRef.instance) {
      this.valueChangeSubscription = this.componentRef.instance.valueChange.subscribe(() => {
        this.valueChange.next();
      });
    }

    if (
      this.componentRef.instance instanceof StringFieldComponent //
      || this.componentRef.instance instanceof NumberFieldComponent //
      || this.componentRef.instance instanceof CheckboxFieldComponent //
      || this.componentRef.instance instanceof PhoneNumberFieldComponent //
      || this.componentRef.instance instanceof CarrierAutoCompleteFieldComponent //
      || this.componentRef.instance instanceof DataProtectionFieldComponent //
      || this.componentRef.instance instanceof CommunicationLanguageFieldComponent //
      || this.componentRef.instance instanceof DictionarySelectFieldComponent
    ) {

      console.log('procesing instance', this.componentRef.instance);

      if ('grow' in this.componentRef.instance) {
        this.componentRef.instance.grow = true;
      }

      if ('disableAutoComplete' in this.componentRef.instance) {
        this.componentRef.instance.disableAutoComplete = this.disableAutoComplete;
      }

      if ('dictionaryRoot' in this.componentRef.instance) {
        this.componentRef.instance.dictionaryRoot = this.field.dictionaryRoot;
      }

      if ('label' in this.componentRef.instance) {
        const label = this.translationService.translate(this.dictionaryPraefix + this.field.attribute);
        this.componentRef.instance.label = label != null ? label : '[' + this.dictionaryPraefix + this.field.attribute + ']';
      }

      if ('required' in this.componentRef.instance) {
        if (this.fieldReference.required === true && !(this.componentRef.instance instanceof CheckboxFieldComponent)) {
          this.componentRef.instance.required = true;
        }
      }


      this.controlValueAccessor = this.componentRef.instance;

    }

    else {
      console.warn('unsupported component type', this.componentRef.instance);
    }

    if (this.controlValueAccessor != null) {
      this.controlValueAccessor.registerOnChange(this.onChangeFn);
      this.controlValueAccessor.registerOnTouched(this.onTouched);
      this.controlValueAccessor.setDisabledState(this.disabled);
      this.controlValueAccessor.writeValue(this.value);
    }

  }

  private createPanelComponent() {

    if (this.field.dataType === DataType.STRING) {
      const factory = this.componentFactoryResolver.resolveComponentFactory(TextLabelComponent);
      this.componentRef = this.contentRef.createComponent(factory);
      this.componentRef.instance.labelDictionaryKey = this.dictionaryPraefix + this.field.attribute;
    }

    else if (this.field.dataType === DataType.NUMBER) {
      const factory = this.componentFactoryResolver.resolveComponentFactory(TextLabelComponent);
      this.componentRef = this.contentRef.createComponent(factory);
      this.componentRef.instance.labelDictionaryKey = this.dictionaryPraefix + this.field.attribute;

    }

    else if (this.field.dataType === DataType.DATE) {
      const factory = this.componentFactoryResolver.resolveComponentFactory(TextLabelComponent);
      this.componentRef = this.contentRef.createComponent(factory);
      this.componentRef.instance.labelDictionaryKey = this.dictionaryPraefix + this.field.attribute;
    }

    else if (this.field.dataType === DataType.DICTIONARY) {
      const factory = this.componentFactoryResolver.resolveComponentFactory(DictionaryFieldComponent);
      this.componentRef = this.contentRef.createComponent(factory);
      this.componentRef.instance.dictionaryKey = this.fieldReference.dictionaryKey;
    }

    else {

      console.error('no support for DataType ', this.field.dataType);
      const factory = this.componentFactoryResolver.resolveComponentFactory(TextLabelComponent);
      this.componentRef = this.contentRef.createComponent(factory);
      this.componentRef.instance.labelDictionaryKey = this.dictionaryPraefix + this.field.attribute;
      this.componentRef.instance.value = 'unsupported data type ' + this.field.dataType;
    }

  }

  private setComponentValue() {
    if (this.field == null) {
      return;
    }

    if (!this.field.isEditable || this.mode !== TransportDetailMode.EDITING) {

      if (this.field.attribute == null) {
        return;
      }

      let value = this.getValue(this.transport, this.field?.attribute.split('.'));

      if (this.field.dataType === DataType.STRING) {
        this.componentRef.instance.value = value;
      }

      else if (this.field.dataType === DataType.NUMBER) {

        if (
          ['current-position-duration', 'waitingTime', 'current-position-statusDuration', 'processingTimeCurrentProcessing']
            .includes(this.field.identifier)) {
          this.componentRef.instance.value = this.timeUtilsService.formatDuration(value);
        } else {
          this.componentRef.instance.value = value;
        }

        if (this.field.identifier === 'processingTime' && this.transport.masterdata.preloading) {
          this.componentRef.instance.classes = 'preloading';
          console.log('adding style preloading');
        } else {
          this.componentRef.instance.classes = '';
          console.log('removing style preloading');
        }
      }

      else if (this.field.dataType === DataType.DATE) {
        this.componentRef.instance.value = this.dateUtilsService.formatTimestampToDeFormat(value, true);
      }

      else if (this.field.dataType === DataType.BOOLEAN) {
        let key = value ? 'common.enum.boolean.yes' : 'common.enum.boolean.no';
        this.componentRef.instance.value = this.translationService.translate(key);
      }

      else {
        this.componentRef.instance.value = value;
      }

    }

    if (this.field.isEditable && this.mode == TransportDetailMode.EDITING && this.field.dataType === DataType.OTHERS) {
      if ('setTransport' in this.componentRef.instance) {
        this.componentRef.instance.setTransport(this.transport);
      } else {
        console.log('cant set transport to component', this.componentRef.instance)
      }
    }

  }


  private getValue(obj: any, path: Array<string>): any {
    if (obj == null || obj == undefined) {
      return null;
    }
    if (path.length > 1) {
      const attribute = path.shift();
      return this.getValue(obj[attribute], path);
    } else {
      return obj[path[0]];
    }
  }

  writeValue(obj: any): void {
    this.value = obj;
    if (this.controlValueAccessor != null) {
      this.controlValueAccessor.writeValue(obj);
    }
  }
  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    if (this.controlValueAccessor != null) {
      this.controlValueAccessor.registerOnChange(fn);
    }
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
    if (this.controlValueAccessor != null) {
      this.controlValueAccessor.registerOnTouched(fn);
    }
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.controlValueAccessor != null) {
      this.controlValueAccessor.setDisabledState(isDisabled);
    }
  }

}
